import { Directive, Input, ElementRef, Renderer2, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { HelpState } from '@enums';
import * as fromHelpStore from '@appStore/help';
import { map, Observable } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { SubSink } from 'subsink';

@Directive({
  selector: '[contextualinfo]',
  standalone: false
})
export class ContextualInfoDirective implements OnInit, OnDestroy {

  svgPath: string = 'sx-info';
  iconSize: string = '20px';
  iconClass: string = 'sx-contextual-info';
  iconPosition: 'after' | 'before' = 'after';
  tooltipText: string;
  tooltipClass: string = 'sx-contextual-info__tooltip';

  // Remove possibility to size icon

  private iconElement: HTMLElement;
  private tooltipElement: HTMLElement | null = null;
  private mouseEnterListener?: () => void;
  private mouseLeaveListener?: () => void;
  private clickListener?: () => void;
  private isMobile = false;
  private subs = new SubSink();

  isMobile$: Observable<boolean> = this.breakpointObserver
      .observe('(max-width: 576px)')
      .pipe(map((result) => result.matches));

  constructor(
    private translateService: TranslateService,
    private el: ElementRef,
    private renderer: Renderer2,
    private breakpointObserver: BreakpointObserver,
    private helpStore: Store<fromHelpStore.State>
  ) {}

  ngOnInit() {

    this.subs.sink = this.isMobile$.subscribe(isMobile => {
      this.isMobile = isMobile;
    });

    if (this.svgPath) {
      this.createSvgIcon();
    }
    this.tooltipText = this.translateService.instant('core.help.menu.contextualInfo');
  }

  ngOnDestroy() {
    // Clean up tooltip
    this.hideTooltip();
    
    if (this.iconElement) {
      this.renderer.removeChild(this.el.nativeElement, this.iconElement);
    }
  }

  private createSvgIcon() {
    // Create div wrapper element and add CSS-class
    this.iconElement = this.renderer.createElement('div');
    this.renderer.addClass(this.iconElement, this.iconClass);
    
    // Create SVG element and add CSS-class
    const svgElement = this.renderer.createElement('svg', 'http://www.w3.org/2000/svg');
    this.renderer.addClass(svgElement, 'icon-svg');
    const useElement = this.renderer.createElement('use', 'http://www.w3.org/2000/svg');
    const iconRef = `#${this.svgPath}`;
    this.renderer.setAttribute(useElement, 'xlink:href', iconRef);
    this.renderer.setAttribute(useElement, 'href', iconRef);
    
    this.renderer.appendChild(svgElement, useElement);
    this.renderer.appendChild(this.iconElement, svgElement);
    
    // Add position-specific CSS class
    this.renderer.addClass(this.iconElement, `position-${this.iconPosition}`);
    // Add size-specific CSS class based on iconSize
    this.renderer.addClass(this.iconElement, 'size-medium'); 
    this.renderer.setStyle(svgElement, 'width', this.iconSize);
    this.renderer.setStyle(svgElement, 'height', this.iconSize);
    
    // Add custom tooltip functionality with event listeners
    this.setupCustomTooltip();
    
    // Ensure the element is focusable for better accessibility
    this.renderer.setAttribute(this.iconElement, 'tabindex', '0');
    
    // Insert icon based on position
    if (this.iconPosition === 'before') {
      this.renderer.insertBefore(this.el.nativeElement, this.iconElement, this.el.nativeElement.firstChild);
    } else {
      this.renderer.appendChild(this.el.nativeElement, this.iconElement);
    }
  }

  private setupCustomTooltip(): void {
    // Create mouse event listeners
    this.mouseEnterListener = () => this.showTooltip();
    this.mouseLeaveListener = () => this.hideTooltip();
    this.clickListener = () => this.openHelp();

    // Add event listeners
    this.renderer.listen(this.iconElement, 'mouseenter', this.mouseEnterListener);
    this.renderer.listen(this.iconElement, 'mouseleave', this.mouseLeaveListener);
    this.renderer.listen(this.iconElement, 'focus', this.mouseEnterListener);
    this.renderer.listen(this.iconElement, 'blur', this.mouseLeaveListener);
    this.renderer.listen(this.iconElement, 'click', this.clickListener);
  }

  private showTooltip(): void {
    if (this.isMobile || this.tooltipElement) return;

    // Create tooltip element
    this.tooltipElement = this.renderer.createElement('div');
    this.renderer.addClass(this.tooltipElement, this.tooltipClass);
    this.renderer.addClass(this.tooltipElement, 'sx-contextual-info__tooltip');
    
    // Add tooltip text
    const textNode = this.renderer.createText(this.tooltipText);
    this.renderer.appendChild(this.tooltipElement, textNode);

    // Calculate position - position tooltip below the icon
    const rect = this.iconElement.getBoundingClientRect();
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    
    this.renderer.setStyle(this.tooltipElement, 'left', `${rect.left + scrollX + (rect.width / 2)}px`);
    this.renderer.setStyle(this.tooltipElement, 'top', `${rect.bottom + scrollY + 8}px`);

    // Add to body
    this.renderer.appendChild(document.body, this.tooltipElement);
  }

  private hideTooltip(): void {
    if (this.tooltipElement) {
      this.renderer.removeChild(document.body, this.tooltipElement);
      this.tooltipElement = null;
    }
  }

  private openHelp(): void {
    // Scroll to top for better help visibility
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
    
    // Open contextual help for the page
    this.helpStore.dispatch(new fromHelpStore.HelpSetState(HelpState.contextualInfo));
  }
}